import React, { useMemo } from 'react';
import { caseStudies } from '../caseStudiesData';
import PortfolioCard from '../components/PortfolioCard';
import AnimatedSection from '../components/AnimatedSection';
import SectionHeading from '../components/SectionHeading';
import SEO from '../components/SEO';
import { Link, useSearchParams } from 'react-router-dom';
import { useI18n } from '../i18n/I18nContext';

type PortfolioFilter = 'all' | 'real-world' | 'showcase';
type SortOption = 'featured' | 'a-z' | 'z-a';

const isPortfolioFilter = (value: string | null): value is PortfolioFilter => {
  return value === 'all' || value === 'real-world' || value === 'showcase';
};

const isSortOption = (value: string | null): value is SortOption => {
  return value === 'featured' || value === 'a-z' || value === 'z-a';
};

function PortfolioPage() {
  const { t, localizedPath } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const filterLabels: Record<PortfolioFilter, string> = {
    all: t.portfolio.filterAllProjects,
    'real-world': t.portfolio.filterRealWorld,
    showcase: t.portfolio.filterShowcase,
  };

  const sortLabels: Record<SortOption, string> = {
    featured: t.portfolio.sortFeatured,
    'a-z': t.portfolio.sortAZ,
    'z-a': t.portfolio.sortZA,
  };

  const filter: PortfolioFilter = isPortfolioFilter(searchParams.get('type')) ? searchParams.get('type') : 'all';
  const sort: SortOption = isSortOption(searchParams.get('sort')) ? searchParams.get('sort') : 'featured';

  const setFilter = (nextFilter: PortfolioFilter) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('type', nextFilter);
    setSearchParams(nextParams, { replace: true });
  };

  const setSort = (nextSort: SortOption) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('sort', nextSort);
    setSearchParams(nextParams, { replace: true });
  };

  const clearControls = () => {
    setSearchParams({}, { replace: true });
  };

  const counts = useMemo(() => {
    const realWorld = caseStudies.filter((project) => project.projectType === 'real-world').length;
    const showcase = caseStudies.filter((project) => project.projectType === 'showcase').length;

    return {
      all: caseStudies.length,
      'real-world': realWorld,
      showcase,
    };
  }, []);

  const filteredProjects = useMemo(() => {
    const projectsByType = filter === 'all'
      ? [...caseStudies]
      : caseStudies.filter((project) => project.projectType === filter);

    if (sort === 'a-z') {
      return projectsByType.sort((a, b) => a.title.localeCompare(b.title));
    }

    if (sort === 'z-a') {
      return projectsByType.sort((a, b) => b.title.localeCompare(a.title));
    }

    return projectsByType;
  }, [filter, sort]);

  const caseStudyTranslations: Record<string, { title: string; category: string; description: string }> = {
    'cleaning-edge-platform': { title: t.caseStudy.items.cleaningEdgePlatform.title, category: t.caseStudy.items.cleaningEdgePlatform.category, description: t.caseStudy.items.cleaningEdgePlatform.description },
    'ai-analytics-dashboard': { title: t.caseStudy.items.aiAnalyticsDashboard.title, category: t.caseStudy.items.aiAnalyticsDashboard.category, description: t.caseStudy.items.aiAnalyticsDashboard.description },
    'global-marketing-campaign': { title: t.caseStudy.items.globalMarketingCampaign.title, category: t.caseStudy.items.globalMarketingCampaign.category, description: t.caseStudy.items.globalMarketingCampaign.description },
    'haunted-galleria': { title: t.caseStudy.items.hauntedGalleria.title, category: t.caseStudy.items.hauntedGalleria.category, description: t.caseStudy.items.hauntedGalleria.description },
  };

  return (
    <div className="container mx-auto px-4 py-20">
      <SEO
        title={t.seo.portfolioTitle}
        description={t.seo.portfolioDescription}
        canonicalPath={localizedPath('/portfolio')}
      />
      <SectionHeading eyebrow={t.portfolio.eyebrow} title={t.portfolio.title} description={t.seo.portfolioDescription} />
      <AnimatedSection animation="fade-in-up" delay={100}>
        <div className="flex justify-center gap-4 mb-12">
          <Link to={localizedPath('/#services')} className="bg-[#FDCB12] text-black px-6 py-3 rounded-md hover:bg-[#FF5500] hover:text-white transition-all duration-300">
            {t.portfolio.viewServices}
          </Link>
          <Link to={localizedPath('/#contact')} className="border-2 border-[#FDCB12] text-[#FDCB12] px-6 py-3 rounded-md hover:bg-[#FDCB12] hover:text-black transition-all duration-300">
            {t.portfolio.startProject}
          </Link>
        </div>
      </AnimatedSection>

      <AnimatedSection animation="fade-in-up" delay={150}>
        <div className="mb-6 flex flex-wrap justify-center gap-3" role="tablist" aria-label="Filter portfolio projects by type">
          {(Object.keys(filterLabels) as PortfolioFilter[]).map((filterKey) => {
            const isActive = filter === filterKey;

            return (
              <button
                key={filterKey}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setFilter(filterKey)}
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-300 ${
                  isActive
                    ? 'border-[#FDCB12] bg-[#FDCB12] text-black'
                    : 'border-zinc-600 text-zinc-300 hover:border-[#FDCB12] hover:text-[#FDCB12]'
                }`}
              >
                {filterLabels[filterKey]} <span className="ml-1 opacity-80">({counts[filterKey]})</span>
              </button>
            );
          })}
        </div>

        <div className="mb-4 text-center text-sm text-zinc-400">
          {t.portfolio.showing} <span className="font-semibold text-zinc-200">{filteredProjects.length}</span> {t.portfolio.of}{' '}
          <span className="font-semibold text-zinc-200">{counts.all}</span> {t.portfolio.projects}
        </div>

        <div className="mb-10 flex flex-wrap items-center justify-center gap-3">
          <label htmlFor="portfolio-sort" className="text-sm text-zinc-300">{t.portfolio.sortLabel}</label>
          <select
            id="portfolio-sort"
            value={sort}
            onChange={(event) => setSort(event.target.value as SortOption)}
            className="rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-[#FDCB12] focus:outline-none"
          >
            {(Object.keys(sortLabels) as SortOption[]).map((sortOption) => (
              <option key={sortOption} value={sortOption}>{sortLabels[sortOption]}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={clearControls}
            className="rounded-md border border-zinc-700 px-3 py-2 text-sm text-zinc-300 transition-colors duration-300 hover:border-[#FDCB12] hover:text-[#FDCB12]"
          >
            {t.portfolio.reset}
          </button>
        </div>
      </AnimatedSection>

      <AnimatedSection animation="fade-in-up" delay={200}>
        {filteredProjects.length === 0 ? (
          <div className="rounded-lg border border-zinc-700 bg-zinc-900/50 p-8 text-center text-zinc-300">
            {t.portfolio.noProjects}
          </div>
        ) : (
          <div className="filter-grid stagger-grid grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => {
              const translated = caseStudyTranslations[project.id];
              return (
                <PortfolioCard
                  key={project.id}
                  title={translated?.title ?? project.title}
                  category={translated?.category ?? project.category}
                  description={translated?.description ?? project.description}
                  image={project.image}
                  caseStudyId={project.id}
                  link={project.projectUrl}
                  projectType={project.projectType}
                />
              );
            })}
          </div>
        )}
      </AnimatedSection>
    </div>
  );
}

export default PortfolioPage;
